import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

const MIN_PASSWORD = 10;

/**
 * The two pages an email link lands on. Both work whether or not the browser
 * is signed in, so they sit outside the login gate.
 */

function tokenFromUrl(): string {
  return new URLSearchParams(window.location.search).get('token') ?? '';
}

/** Leaves the link page for the app, dropping the token from the address bar. */
function goHome() {
  window.location.replace('/');
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="logo auth-logo">
          <span className="logo-mark" aria-hidden>Δ</span>
          DeltaPicks
        </div>
        <p className="auth-sub">{title}</p>
        {children}
      </div>
    </div>
  );
}

export function VerifyEmail() {
  const { adoptUser } = useAuth();
  const [state, setState] = useState<'working' | 'done' | 'failed'>('working');
  const [error, setError] = useState('');
  // StrictMode runs effects twice in dev; a token can only be spent once.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api
      .verifyEmail(tokenFromUrl())
      .then((r) => {
        if (r.user) adoptUser(r.user);
        setState('done');
      })
      .catch((err: Error) => {
        setError(err.message);
        setState('failed');
      });
  }, [adoptUser]);

  return (
    <Shell title="Confirming your email">
      {state === 'working' && <div className="dim" style={{ textAlign: 'center' }}>One moment…</div>}
      {state === 'done' && <div className="auth-ok">Your email is confirmed. Thanks!</div>}
      {state === 'failed' && (
        <div className="auth-error">
          {error}. Sign in and use “Resend link” on the banner to get a new one.
        </div>
      )}
      {state !== 'working' && (
        <button className="btn btn-primary" style={{ width: '100%' }} onClick={goHome}>
          Continue to DeltaPicks
        </button>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const { adoptUser } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) return setError(`Password must be at least ${MIN_PASSWORD} characters`);
    if (password !== confirm) return setError('The two passwords do not match');

    setBusy(true);
    try {
      const { user } = await api.resetPassword(tokenFromUrl(), password);
      adoptUser(user);
      goHome();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setBusy(false);
    }
  }

  return (
    <Shell title="Choose a new password. You will be signed out everywhere else.">
      <form onSubmit={submit}>
        <label className="field">
          <span className="label-text">New password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            aria-invalid={error != null}
            required
            autoFocus
          />
          <span className="label-hint">At least {MIN_PASSWORD} characters.</span>
        </label>
        <label className="field">
          <span className="label-text">Confirm password</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            aria-invalid={error != null}
            required
          />
        </label>
        {error && <div className="auth-error">{error}</div>}
        <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
          {busy ? 'Saving…' : 'Save password and sign in'}
        </button>
      </form>
    </Shell>
  );
}
